import { Component } from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import cn from 'classnames';
import PropTypes from 'prop-types';

import { err } from 'actions/errors';
import { fetchAllProductStyles, sendNotificationSubscriptions } from 'actions/productdetail/sharing';
import { INTERNET_EMAIL } from 'common/regex';
import { capitalize } from 'helpers/lodashReplacement';
import ProductUtils from 'helpers/ProductUtils';
import BackInStockImage from 'images/back_in_stock_illustration.svg';
import modalStyles from 'styles/components/common/modal.scss';
import css from 'styles/components/productdetail/productNotify.scss';

const regex = INTERNET_EMAIL.toString().slice(2, -2);
const initialState = { emailAddressValid: true, hasSubmittedInvalid: false };
const DONE_MESSAGE = <p role="alert">You have been added to the list! We will notify you when we get this item back in stock.</p>;

function makeSizeField(dimension, values, initialValue) {
  const dimensionId = `d${dimension.id}`;
  const dimensionName = dimension.name;
  const fieldId = `productNotifyMeDimension-${dimensionId}`;
  return (
    <fieldset key={dimensionId}>
      <label htmlFor={fieldId}>{capitalize(dimensionName)}</label>
      {values.length === 1 ? (
        <>
          <span>{values[0].value}</span>
          <input
            type="hidden"
            id={fieldId}
            name={dimensionId}
            value={values[0].id}/>
        </>
      ) : (
        <select id={fieldId} name={dimensionId} defaultValue={initialValue}>
          {values.map(({ id, value }) => <option key={id} value={id}>{value}</option>)}
        </select>
      )}
    </fieldset>
  );
}

function makeSizingFields(sizing, initialSizing) {
  return (
    <>
      {sizing.dimensions.map((dimension, index) => makeSizeField(dimension, sizing.allUnits[index].values, initialSizing[`d${dimension.id}`]))}
    </>
  );
}

const validEmailMessage = <p className={css.error} id="errorMsg">Oops, <strong>enter your email</strong> so we can contact you when this item is back in stock.</p>;

export class ProductNotifyModal extends Component {
  state = initialState;

  componentDidMount() {
    const { isOpen, productId, productNotify: { product }, fetchAllProductStyles } = this.props;
    if (isOpen && !product) {
      fetchAllProductStyles(productId);
    }
  }

  componentDidUpdate(prevProps) {
    const { prevIsOpen, productNotify: { product } } = prevProps;
    const { isOpen, productId, fetchAllProductStyles } = this.props;
    if (isOpen && !prevIsOpen && !product) {
      fetchAllProductStyles(productId);
    }
  }

  close = () => {
    this.props.onClose();
    this.setState(initialState);
  }

  send = e => {
    const { productNotify: { product: { brandId, sizing } }, sendNotificationSubscriptions } = this.props;
    const { emailAddress, brandNotify, newsletter, color } = e.currentTarget;

    e.preventDefault();
    // do not rely on emailAddressValid from state since blur handler may not have fired yet.
    if (emailAddress && emailAddress.checkValidity()) {
      // let's lookup the stockId based on what the user has selected.
      const dimensionFields = Object.keys(sizing.dimensionIdToName);

      const colorId = color.value;
      const dimensionValues = {};
      dimensionFields.forEach(fieldName => {
        dimensionValues[fieldName] = e.currentTarget[fieldName].value;
      });

      const stock = ProductUtils.getStockBySize(sizing.stockData, colorId, dimensionValues, true);
      // TODO if this combination is actually in stock should we show something?
      sendNotificationSubscriptions(emailAddress.value, stock && stock.id, newsletter.checked, brandNotify.checked ? brandId : null);
    } else {
      this.setState({ hasSubmittedInvalid: true });
    }

  }

  validateEmail = ({ currentTarget }) => {
    this.setState({ emailAddressValid: currentTarget.checkValidity() });
  }

  makeMessageContent(isSuccess) {
    const { testId } = this.context;
    return (
      <div>
        <div className={css.messageContent}>
          <div className={cn({ [css.complete]: isSuccess, [css.error]: !isSuccess })} data-test-id={testId('notifyDoneMessage')}>{isSuccess ? DONE_MESSAGE : err.ERROR_ADDING_TO_EMAIL_LIST}</div>
          <img src={BackInStockImage} alt="Back in Stock" className={css.imageFlair}/>
        </div>
        <div className={css.actionBar}>
          <button
            type="button"
            onClick={this.close}
            className={css.sendButton}
            data-test-id={testId('notifyClose')}
          >Close</button>
        </div>
      </div>
    );
  }

  makeModalContent() {
    // note we're using product under `productNotify` since it has all OOS sizes.
    const { productNotify: { sending, successfullySent, product }, productId, colorId, selectedSizing } = this.props;
    const { testId } = this.context;
    const { emailAddressValid, hasSubmittedInvalid } = this.state;
    if (typeof successfullySent === 'boolean') {
      return this.makeMessageContent(successfullySent);
    } else {
      return (
        <div>
          <form
            method="post"
            onReset={this.close}
            onSubmit={this.send}
            noValidate>
            <div className={css.productNotifyContainer}>
              <div>Enter your email below and we’ll let you know when the combination you are looking for becomes available!</div>
              {hasSubmittedInvalid && validEmailMessage}
              <fieldset>
                <label htmlFor="productNotifyEmailAddress" className={cn({ [css.labelInvalid]: !emailAddressValid })}>Your Email</label>
                <input
                  type="email"
                  id="productNotifyEmailAddress"
                  className={cn(css.emailAddress, { [css.invalid]: !emailAddressValid })}
                  name="emailAddress"
                  pattern={regex}
                  required
                  data-test-id={testId('notifyEmail')}
                  aria-describedby="errorMsg"
                  onBlur={this.validateEmail}
                />
              </fieldset>
              <fieldset>
                <label htmlFor="productNotifyColor">Color</label>
                <select
                  id="productNotifyColor"
                  name="color"
                  required={true}
                  defaultValue={colorId}>
                  {product.styles.map(({ colorId, color }) => <option key={colorId} value={colorId}>{color}</option>)}
                </select>
              </fieldset>
              {makeSizingFields(product.sizing, selectedSizing)}
              <div className={css.listOptions}>
                <div>
                  <fieldset>
                    <input
                      type="checkbox"
                      id="productNotifyNewsletter"
                      className={css.checkBox}
                      name="newsletter"
                      data-test-id={testId('shamelessPlugCheckbox')}/>
                    <label htmlFor="productNotifyNewsletter">Yes! Please subscribe me to the Weekly Shameless Plug, the newsletter with new brand information, trends, and the latest styles. </label>
                  </fieldset>

                  <fieldset>
                    <input
                      type="checkbox"
                      id="productNotifyBrand"
                      className={css.checkBox}
                      name="brandNotify"
                      data-test-id={testId('brandNotifyCheckbox')}/>
                    <label htmlFor="productNotifyBrand">Yes! Please notify me whenever you add new styles of this brand.</label>
                  </fieldset>
                </div>
                <img src={BackInStockImage} alt="Back in Stock" className={css.imageFlair}/>
              </div>
              <input type="hidden" value={productId} name="productId"/>
              <input type="hidden" value={colorId} name="colorId"/>
            </div>
            <div className={css.actionBar}>
              <button
                type="reset"
                className={css.closeButton}
                disabled={sending}
                data-test-id={testId('cancelButton')}>Cancel</button>
              <button
                type="submit"
                className={css.sendButton}
                disabled={sending}
                data-test-id={testId('notifyButton')}>{sending ? 'Working..' : 'notify me'}</button>
            </div>
          </form>
        </div>
      );
    }
  }

  render() {
    const { isOpen, productNotify: { product } } = this.props;
    const { testId } = this.context;
    return (
      <Modal
        isOpen={isOpen}
        overlayClassName={modalStyles.modalOverlay}
        className={cn(modalStyles.modalContent, css.modalContent)}
        contentLabel="Product Notify Me Popup"
        bodyOpenClassName={css.modalOpenBodyStyle}>
        <div data-test-id={testId('notifyMeOos')}>
          <div className={modalStyles.header}>
            <h2>Notify Me When This Item is Back in Stock</h2>
            <button
              type="button"
              className={modalStyles.close}
              onClick={this.close}
              aria-label="Close"/>
          </div>
          {product && this.makeModalContent()}
        </div>
      </Modal>
    );
  }
}

ProductNotifyModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  productNotify: PropTypes.object.isRequired,
  productId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  selectedSizing: PropTypes.object.isRequired
};

ProductNotifyModal.contextTypes = {
  testId: PropTypes.func.isRequired
};

function mapStateToProps(state) {
  return {
    productNotify: state.sharing.productNotify
  };
}

export default connect(mapStateToProps, { fetchAllProductStyles, sendNotificationSubscriptions })(ProductNotifyModal);
